import { find } from 'lodash';
import { createSDKAction, onPending, onFulfill, onReject } from '../util';
import ActionTypes from '../action-types';
import socket from '../../sdk/services/socket';
import { startCall as deviceStartCall } from '../../device';
import { BROADBAND_CALL_CODE, CALL_STATUS, RECORDING_STATUS } from '../../../Constants';

export function startCall(taskId, jobTypeCode) {
  if (jobTypeCode === BROADBAND_CALL_CODE) {
    return createSDKAction(ActionTypes.START_CALL, {
      callback: (sdk, dispatch) => {
        dispatch({ type: ActionTypes.START_CALL });

        deviceStartCall(taskId);

        return Promise.resolve();
      }
    });
  }

  return createSDKAction(ActionTypes.START_CALL, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.START_CALL) });
      return sdk
        .api(`/task/${taskId}/call`, {
          requiresAuthentication: true,
          method: 'post'
        })
        .then(response => {
          dispatch({ type: onFulfill(ActionTypes.START_CALL), payload: response.body });

          return Promise.resolve(response.body);
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.START_CALL), payload: err });

          return Promise.reject(err);
        });
    }
  });
}

export function getLastCallId(taskId) {
  return createSDKAction(ActionTypes.GET_CALL_ID, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.GET_CALL_ID) });
      return sdk
        .api(`/task/${taskId}/call`, {
          requiresAuthentication: true,
          method: 'get'
        })
        .then(response => {
          dispatch({ type: onFulfill(ActionTypes.GET_CALL_ID), payload: response.body });

          return Promise.resolve(response.body);
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.GET_CALL_ID), payload: err });

          return Promise.reject(err);
        });
    }
  });
}

export function startRecording(taskId, callId) {
  return createSDKAction(ActionTypes.START_RECORDING, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.START_RECORDING) });

      return sdk
        .api(`/task/${taskId}/call/${callId}/startRecording`, {
          requiresAuthentication: true,
          method: 'post'
        })
        .then(response => {
          dispatch({ type: onFulfill(ActionTypes.START_RECORDING), payload: response.body });

          return Promise.resolve(response.body);
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.START_RECORDING), payload: err });

          return Promise.reject(err);
        });
    }
  });
}

export function stopCall(taskId, callId) {
  return createSDKAction(ActionTypes.STOP_CALL, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.STOP_CALL) });

      return sdk
        .api(`/task/${taskId}/call/${callId}/stop`, {
          requiresAuthentication: true,
          method: 'post'
        })
        .then(response => {
          dispatch({ type: onFulfill(ActionTypes.STOP_CALL), payload: response.body });

          return Promise.resolve(response.body);
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.STOP_CALL), payload: err });

          return Promise.reject(err);
        });
    }
  });
}

export function stopRecording(taskId, callId) {
  return createSDKAction(ActionTypes.STOP_RECORDING, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.STOP_RECORDING) });

      return sdk
        .api(`/task/${taskId}/call/${callId}/stopRecording`, {
          requiresAuthentication: true,
          method: 'post'
        })
        .then(response => {
          dispatch({ type: onFulfill(ActionTypes.STOP_RECORDING), payload: response.body });

          return Promise.resolve();
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.STOP_RECORDING), payload: err });

          return Promise.reject(err);
        });
    }
  });
}

export function completeCall() {
  return createSDKAction(ActionTypes.COMPLETE_CALL, {
    callback: (sdk, dispatch) => {
      dispatch({ type: ActionTypes.COMPLETE_CALL });

      return Promise.resolve();
    }
  });
}

export function wrapTask() {
  return createSDKAction(ActionTypes.WRAPPING_TASK, {
    callback: (sdk, dispatch) => {
      dispatch({ type: ActionTypes.WRAPPING_TASK });

      return Promise.resolve();
    }
  });
}

export function getCallStatus(taskId, callId) {
  return createSDKAction(ActionTypes.GET_CALL, {
    callback: (sdk, dispatch) => {
      dispatch({ type: onPending(ActionTypes.GET_CALL) });

      return sdk
        .api(`/task/${taskId}/call/${callId}`, {
          requiresAuthentication: true
        })
        .then(response => {
          const callStatus = find(CALL_STATUS, ({ name }) => response.body.callStatus === name);
          const recordingStatus = find(
            RECORDING_STATUS,
            (code, recordingStatusName) =>
              response.body.recordingStatus &&
              response.body.recordingStatus.toUpperCase() === recordingStatusName
          );

          dispatch({
            type: onFulfill(ActionTypes.GET_CALL),
            payload: {
              taskCallId: response.body.taskCallId,
              callStatus: callStatus ? callStatus.code : null,
              recordingStatus: !Number.isNaN(recordingStatus) ? recordingStatus : null
            }
          });

          return response.body;
        })
        .catch(err => {
          dispatch({ type: onReject(ActionTypes.GET_CALL), payload: err });

          return Promise.reject(err);
        });
    }
  });
}

/**
 * Synchronises a SignalR stream with local Redux
 * the call status and call recording status for the current call
 */
export function liveCallStatus() {
  return createSDKAction(ActionTypes.GET_CALL, {
    callback: async (sdk, dispatch, getState) => {
      const { accessToken } = await sdk.getTokens();

      const connection = await socket(`${window._env_.API_HUB_URL}/call`, accessToken);

      connection.on('changeCallStatus', payload => {
        const { taskId } = getState().tasks.currentTask.task;

        if (taskId !== payload.taskId) {
          console.warn(
            `Websocket task id '${payload.taskId}' did not match current task id '${taskId}'`
          );
          return;
        }

        dispatch({
          type: onFulfill(ActionTypes.GET_CALL),
          payload: {
            taskCallId: payload.taskCallId,
            callStatus: payload.taskCallStatus,
            recordingStatus: payload.taskCallRecordingStatus
          }
        });
      });

      await connection;

      return () => connection.stop();
    }
  });
}
